import { ColumnDef } from "@tanstack/react-table";
import { Table } from "@win-reality/react-ui";
import { ChevronRight } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { selectEventQueue } from "../Tagging/store/slices/socket/selectors";
import type { RootState } from "../Tagging/store/store";
import type { IQueueEvent } from "../Tagging/store/types";

// ==================== Helper Functions ====================

const formatNumber = (
  value: number | undefined | null,
  decimals: number = 1,
  fallback: string = "-",
): string => {
  return value != null ? value.toFixed(decimals) : fallback;
};

const convertDegreesToClockPosition = (degrees: number): string => {
  const offsetDegrees = (degrees + 15) % 360;
  const hours = Math.floor(offsetDegrees / 30);
  const minutes = Math.floor((offsetDegrees % 30) * 2);
  return `${hours}:${minutes.toString().padStart(2, "0")}`;
};

// ==================== Row Type ====================

type PitchHistoryRow = {
  id: string;
  velocity: string;
  spinRate: string;
  efficiency: string;
  tilt: string;
  iVertBreak: string;
  horzBreak: string;
  exitVelo: string;
  launchAngle: string;
  distance: string;
};

function eventToRow(event: IQueueEvent, index: number): PitchHistoryRow {
  const tilt = event.pitch_data?.SpinAxisDegrees;
  return {
    id: event.event_uuid || String(index),
    velocity: formatNumber(event.pitch_data?.RelSpeedMPH),
    spinRate: formatNumber(event.pitch_data?.SpinRateRPM),
    efficiency: formatNumber(event.pitch_data?.yt_SpinEfficiencyPercent),
    tilt: tilt != null ? convertDegreesToClockPosition(tilt) : "-",
    iVertBreak: formatNumber(event.pitch_data?.InducedVertBreakInches),
    horzBreak: formatNumber(event.pitch_data?.HorzBreakInches),
    exitVelo: formatNumber(event.hit_data?.ExitSpeedMPH),
    launchAngle: formatNumber(event.hit_data?.AngleDegrees),
    distance: formatNumber(event.hit_data?.DistanceFeet),
  };
}

// ==================== Column Definitions ====================

const columns: ColumnDef<PitchHistoryRow>[] = [
  {
    header: "Velocity",
    accessorKey: "velocity",
  },
  {
    header: "Spin Rate",
    accessorKey: "spinRate",
  },
  {
    header: "Efficiency",
    accessorKey: "efficiency",
  },
  {
    header: "Tilt",
    accessorKey: "tilt",
  },
  {
    header: "I. Vert Break",
    accessorKey: "iVertBreak",
  },
  {
    header: "Horz Break",
    accessorKey: "horzBreak",
  },
  {
    header: "Exit Velo",
    accessorKey: "exitVelo",
  },
  {
    header: "Launch Angle",
    accessorKey: "launchAngle",
  },
  {
    header: "Proj. Distance",
    accessorKey: "distance",
  },
];

// Mobile-visible columns (shown in main row)
const MOBILE_KEYS = new Set(["velocity", "spinRate", "exitVelo"]);

const mobileColumns: ColumnDef<PitchHistoryRow>[] = [
  ...columns.filter((col) =>
    MOBILE_KEYS.has((col as { accessorKey: string }).accessorKey),
  ),
  {
    id: "expand",
    header: "",
    cell: () => (
      <ChevronRight className="h-4 w-4 text-gray-400 transition-transform" />
    ),
    size: 40,
  },
];

const hiddenColumnDefs = columns.filter(
  (col) => !MOBILE_KEYS.has((col as { accessorKey: string }).accessorKey),
);

// ==================== Mobile Expandable Row ====================

const ExpandableRowContent = ({
  row,
}: {
  row: { original: PitchHistoryRow };
}) => {
  return (
    <div className="grid grid-cols-3 gap-x-3 gap-y-2 px-4 py-3">
      {hiddenColumnDefs.map((col) => {
        const key = (col as { accessorKey: string })
          .accessorKey as keyof PitchHistoryRow;
        return (
          <div key={key} className="flex flex-col gap-0.5">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">
              {col.header as string}
            </span>
            <span className="text-sm text-text-light">{row.original[key]}</span>
          </div>
        );
      })}
    </div>
  );
};

// ==================== Main Component ====================

export const PitchHistory = () => {
  const eventQueue = useSelector((state: RootState) => selectEventQueue(state));

  const [pitchHistoryData, setPitchHistoryData] = useState<IQueueEvent[]>([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Snapshot event queue on mount (last 50)
  useEffect(() => {
    const recentEvents = eventQueue.slice(0, 50);
    setPitchHistoryData(recentEvents);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const tableData = useMemo(
    () => pitchHistoryData.map((event, index) => eventToRow(event, index)),
    [pitchHistoryData],
  );

  if (pitchHistoryData.length === 0) {
    return (
      <div className="flex w-full items-center justify-center p-10">
        <p className="text-xl font-semibold uppercase text-gray-300">
          No pitch history found
        </p>
      </div>
    );
  }

  return (
    <div className="px-4 pb-6 md:px-6">
      <Table
        size="compact"
        data={tableData}
        columns={isMobile ? mobileColumns : columns}
        total={tableData.length}
        headerClassName="text-xs font-semibold text-text-light uppercase"
        cellClassName="text-sm text-text-light"
        renderExpandableRowComponent={
          isMobile ? (row) => <ExpandableRowContent row={row} /> : undefined
        }
      />
    </div>
  );
};

export default PitchHistory;
